import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';

let registered = false;

/**
 * Logs unhandled promise rejections and uncaught errors through our logger.
 *
 * Without this, browsers report stray rejections on their own schedule and in a
 * nearly untraceable form — Firefox defers the report until the rejected promise
 * is garbage-collected and renders non-Error values as a bare
 * `uncaught exception: Object` with no message, stack, or source. Logging at
 * rejection time preserves the value, a timestamp that lines up with the rest of
 * the log stream, and ships it to Datadog via the logger transports.
 *
 * Works in both window and worker contexts (both global scopes emit these events).
 */
export function registerGlobalErrorLogging(logger: Logger): void {
    if (registered) return;
    registered = true;

    const log = logger.getSubLogger({ name: 'global-errors' });

    globalThis.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
        const reason: unknown = event.reason;
        const message = reason instanceof Error ? reason.message : String(reason);
        log.error(`Unhandled promise rejection: ${message}`, { reason });
    });

    globalThis.addEventListener('error', (event: ErrorEvent) => {
        // Resource-load errors dispatch a plain Event on the element, not here;
        // this sees only uncaught script errors.
        log.error(`Uncaught error: ${event.message}`, {
            error: event.error as unknown,
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno,
        });
    });
}
